import HBIcon from "@/components/HBIcon";
import Image from "@/components/Image";
import { TrackerA } from "@/components/Tracker";
import imageUrl from "@/utils/imageUrl";
import classNames from "classnames";
import { HTMLAttributes, useCallback, useMemo } from "react";
import { Case, Default, Switch } from "react-if";
import styles from "./cardD.module.less";

const MAX_PINS_TO_CHECK = 10;

enum pinType {
  overlong = "overlong", // 超长竖图，三张纵向排列 (比例 < 0.46)
  long = "long", // 竖图，两张纵向排列 (比例在 (0.75, 0.56])
  square = "square", // 方图，四张横向排列 (比例 = 1)
  short = "short", // 横图，两张横向排列 (比例 > 1)
  overshort = "overshort", // 超长  横图，两张横向排列 (比例 >= 2.35)
  medium = "medium", // 中等图，三张左右排列 (比例在 (1, 0.75])
}

export enum Radius {
  all = "all", // 图片全部圆角
  outer = "outer", // 图片外圆角
}

export enum ImageMargin {
  small = "small", // 图片间距小
  normal = "normal", // 图片间距大
}

interface IProps extends HTMLAttributes<HTMLAnchorElement> {
  pinCount: number;
  pins: IPin[];
  filter: {
    id: number;
    name: string;
  };
  activeFestival?: string;
  radius?: Radius;
  margin?: ImageMargin;
  link?: string;
  title?: string;
}

export function MaterialTopicCard(props: IProps) {
  const {
    title,
    pinCount,
    pins,
    filter,
    activeFestival,
    radius = "all",
    margin = "normal",
    link,
    ...rest
  } = props;

  // 根据比例获取图片类型和数量
  const getImageTypeAndCount = (width, height) => {
    const proportion = width / height;

    if (proportion >= 2.35) {
      return { type: pinType.overshort, count: 2 };
    } else if (proportion > 1) {
      return { type: pinType.short, count: 2 };
    } else if (proportion === 1) {
      return { type: pinType.square, count: 4 };
    } else if (proportion >= 0.75) {
      return { type: pinType.medium, count: 3 };
    } else if (proportion > 0.56) {
      return { type: pinType.long, count: 2 };
    } else {
      return { type: pinType.overlong, count: 3 };
    }
  };

  const groupImagesByProportion = (pins) => {
    if (!pins?.length) {
      return { imgType: pinType.long, imageGroup: [] };
    }

    const maxPins = Math.min(MAX_PINS_TO_CHECK, pins.length);
    const proportionGroups = new Map();

    // 遍历所有图片，按比例分组
    for (let i = 0; i < maxPins; i++) {
      const pin = pins[i];
      if (pin?.file?.width && pin?.file?.height) {
        const { type: imgType } = getImageTypeAndCount(
          pin.file.width,
          pin.file.height
        );

        if (imgType !== undefined) {
          if (!proportionGroups.has(imgType)) {
            proportionGroups.set(imgType, []);
          }
          proportionGroups.get(imgType).push(pin);
        }
      }
    }

    // 按优先级顺序查找合适的图片组
    const priorityOrder = [
      pinType.short,
      pinType.square,
      pinType.medium,
      pinType.long,
      pinType.overlong,
    ];

    for (const imgType of priorityOrder) {
      const group = proportionGroups.get(imgType);

      if (group && group.length > 0) {
        const { count: requiredCount } = getImageTypeAndCount(
          group[0].file.width,
          group[0].file.height
        );

        if (group.length >= requiredCount) {
          return {
            imgType,
            imageGroup: group.slice(0, requiredCount),
          };
        }
      }
    }

    return { imgType: pinType.medium, imageGroup: pins.slice(0, 3) };
  };

  const imageGroupData = useMemo(() => {
    return groupImagesByProportion(pins);
  }, [pins]);

  const { imgType, imageGroup } = imageGroupData;

  // 免抠素材使用棋盘格
  const onImgLoad = useCallback((evt) => {
    const target = evt.target;
    target?.classList.add("transparent-img-bg");
  }, []);

  return (
    //eslint-disable-next-line react/jsx-no-target-blank
    <TrackerA
      className={styles.content}
      href={
        link || `/calendars/${activeFestival}?filter_ids=festival-${filter?.id}`
      }
      target={"_blank"}
      {...rest}
    >
      <div
        className={classNames(styles.cover, {
          [styles.outerRadius]: radius === Radius.outer,
          [styles.smallMargin]: margin === ImageMargin.small,
        })}
      >
        <Switch>
          <Case condition={imgType === pinType.overshort}>
            <div className={styles.overshort}>
              {imageGroup?.map((item) => {
                return (
                  <div key={item.pin_id} className={styles.img}>
                    <Image
                      src={imageUrl(item.file, { width: 240 })}
                      alt=""
                      onLoad={onImgLoad}
                    />
                  </div>
                );
              })}
            </div>
          </Case>
          <Case condition={imgType === pinType.short}>
            <div className={styles.short}>
              {imageGroup?.map((item) => {
                return (
                  <div key={item.pin_id} className={styles.img}>
                    <Image
                      src={imageUrl(item.file, { width: 300 })}
                      alt=""
                      onLoad={onImgLoad}
                    ></Image>
                  </div>
                );
              })}
            </div>
          </Case>
          <Case condition={imgType === pinType.square}>
            <div className={styles.square}>
              {imageGroup?.map((item) => {
                return (
                  <div key={item.pin_id} className={styles.img}>
                    <Image
                      src={imageUrl(item.file, { width: 200, rect: "sq" })}
                      alt=""
                      onLoad={onImgLoad}
                    ></Image>
                  </div>
                );
              })}
            </div>
          </Case>
          <Case condition={imgType === pinType.long}>
            <div className={styles.long}>
              {imageGroup?.map((item) => {
                return (
                  <div
                    key={item.pin_id}
                    className={classNames(styles.img, {
                      [styles.longImg]: imgType === pinType.long,
                    })}
                  >
                    <Image
                      src={imageUrl(item.file, { width: 87 })}
                      alt=""
                      onLoad={onImgLoad}
                    />
                  </div>
                );
              })}
            </div>
          </Case>
          <Case condition={imgType === pinType.overlong}>
            <div className={styles.overlong}>
              {imageGroup?.map((item) => {
                return (
                  <div
                    key={item.pin_id}
                    className={classNames(styles.img, {
                      [styles.overlongImg]: imgType === pinType.overlong,
                    })}
                  >
                    <Image
                      src={imageUrl(item.file, { width: 193 })}
                      alt=""
                      onLoad={onImgLoad}
                    ></Image>
                  </div>
                );
              })}
            </div>
          </Case>
          <Default>
            <div className={styles.medium}>
              {imageGroup?.map((item, index) => {
                return (
                  <div
                    key={item.pin_id}
                    className={classNames(styles.img, {
                      [styles.mediumImg]: imgType === pinType.medium,
                    })}
                  >
                    <Image
                      src={imageUrl(item.file, {
                        width: index === 0 ? 240 : 87,
                      })}
                      alt=""
                      onLoad={onImgLoad}
                    />
                  </div>
                );
              })}
            </div>
          </Default>
        </Switch>
      </div>
      <div className={styles.footer}>
        <div className={styles.left} title={title || filter?.name}>
          {title || filter?.name}
        </div>
        <div className={styles.right}>
          {pinCount}张
          <HBIcon type="ic_chevron_right" className={styles.icon} />
        </div>
      </div>
    </TrackerA>
  );
}
